import React from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import hoistNonReactStatic from 'hoist-non-react-statics';
import { BODY, FOOTER } from 'utils/sectionTypes';
import contextMenu from './contextMenu';
import style from './section.scss';

const EditIcon = () => (<i className="fa fa-fw fa-cog" />);

/**
 * This is a decorator for setting up a Section component.
 *
 * @param {Object} details - an object that describes basic details about this section
 * @param {string=} details.name - the section's name
 * @param {string=} details.icon - the section's icon
 * @param {string=} details.region - the region this section can go in
 * @param {string=} details.flipper - a flipper constant to put this section behind
 * @param {({label: String, iconClass: String, action: function}[]|function(props: Object): Object[])=} details.menu - an array of context menu items or a function that returns the array
 * @param {Object=} options - section options
 * @param {Object[]} options.subsections - describes the subsections of this section
 * @param {Array} options.defaults - the default widgets
 * @param {function} options.resolve - called when trying to resolve this section's requirements
 * @param {bool} options.hideLabel - whether or not to hide the
 * @param {bool} isUserHyper - whether or not the user has hyper access
 */
export default (
  details = {},
  {
    subsections = [], defaults = [], resolve = null, hideLabel = false, menu = {},
  } = {},
) => {
  const createHoc = (Component) => {
    const name = Component.displayName || Component.name || 'SectionComponent';
    const manifest = {
      name,
      icon: 'http://placehold.it/50x50',
      region: BODY,
      ...details,
      subsections,
      defaultWidgets: defaults,
    };
    delete manifest.menu; // The manifest doesn't need this, so let's not clutter things

    class Section extends React.Component {
      editable = (component, rest) => {
        let MenuComponent = false;
        if (menu !== false) {
          MenuComponent = contextMenu((...args) => {
            let menuOpts = menu;
            if (typeof menu === 'function') {
              menuOpts = menu(...args);
            }

            return {
              items: ['delete'],
              heading: 'Options',
              ...menuOpts,
            };
          })(EditIcon);
        }

        return (
          <div className={style.wrapper}>
            {(!hideLabel || MenuComponent) && (
              <div className={style.label}>
                {hideLabel ? false : manifest.name}
                {' '}
                {MenuComponent && (
                  <MenuComponent
                    id={rest.id}
                    idPostfix={this.props.idPostfix}
                    onWidgetEditStart={rest.onEditStart}
                    onDelete={rest.onDelete}
                    onSwapUp={rest.onSwapUp}
                    onSwapDown={rest.onSwapDown}
                    isEditable={this.props.isEditable}
                    isEditing={rest.isEditing}
                    sectionWidgetMoving={rest.sectionWidgetMoving}
                    showSwapArrows={rest.showSwapArrows}
                    {...rest}
                  />
                )}
              </div>
            )}
            {component}
          </div>
        );
      }

      render() {
        const { isEditable = false, idPostfix, isBuilderView, ...rest } = this.props;
        const componentProps = { ...rest, isEditable, isBuilderView };
        const isFooter = manifest.region.includes(FOOTER);
        const component = (
          isFooter ?
            (
              <div className={isBuilderView && 'pb-section'}>
                <Component {...componentProps} />
              </div>
            ) :
            (
              <div className={cx('theme', isBuilderView && 'pb-section')}>
                <Component {...componentProps} />
              </div>
            )
        );

        return isEditable ? this.editable(component, rest) : component;
      }
    }

    Section.propTypes = {
      id: PropTypes.string,
      idPostfix: PropTypes.string,
      isEditable: PropTypes.bool,
      isEditing: PropTypes.bool,
      onEditStart: PropTypes.func,
      onDelete: PropTypes.func,
      onSwapUp: PropTypes.func,
      onSwapDown: PropTypes.func,
      isBuilderView: PropTypes.bool,
    };

    Section.details = manifest;

    Section.displayName = `Section(${name})`;

    if (resolve) {
      Section.resolve = resolve;
    }

    return hoistNonReactStatic(Section, Component);
  };

  if (process.env.NODE_ENV === 'testing') {
    createHoc.$$voyagerTestWhitelist = true;
  }

  return createHoc;
};
